import type { ToolDefinition } from './provider';

export const PROPOSE_CHANGES = 'propose_changes';

/**
 * The one tool Cue gives the model. Property order matters: models stream arguments in schema order, so the
 * code arrives first and the live preview in the editor starts as early as possible.
 */
export const proposeChangesTool: ToolDefinition = {
  name: PROPOSE_CHANGES,
  description:
    "Propose new contents for this card's code, its spec, or both. Send complete contents, never a fragment or a diff: " +
    'each field you send replaces that whole document, and each field you leave out stays as it is. ' +
    'The user reviews the proposal as a diff and accepts or rejects it.',
  inputSchema: {
    type: 'object',
    properties: {
      language: {
        type: 'string',
        description: 'Language of the code, for example python, javascript, html, matlab. Required when code is sent.',
      },
      code: {
        type: 'string',
        description: 'The complete new code for the editor. Leave out to keep the code as it is.',
      },
      spec: {
        type: 'string',
        description: 'The complete new spec in Markdown. Leave out to keep the spec as it is.',
      },
      summary: {
        type: 'string',
        description: 'One or two sentences on what changed and why.',
      },
    },
    required: ['summary'],
    additionalProperties: false,
  },
};

export interface ProposedChanges {
  summary: string;
  code?: string;
  language?: string;
  spec?: string;
}

/** Validate tool input. Null values count as absent (strict schemas send null for fields left out). */
export function parseProposedChanges(input: unknown): ProposedChanges | null {
  if (!input || typeof input !== 'object') return null;
  const record = input as Record<string, unknown>;
  const text = (key: string) => (typeof record[key] === 'string' ? (record[key] as string) : undefined);
  const code = text('code');
  const spec = text('spec');
  if (code === undefined && spec === undefined) return null;
  return {
    summary: text('summary') ?? '',
    ...(code !== undefined ? { code, language: text('language') ?? '' } : {}),
    ...(spec !== undefined ? { spec } : {}),
  };
}
